import { ConversationParticipantEntity } from '@provision/conversation-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import { PlatformUserAvatar } from 'src/components/PlatformUserAvatar/platform-user-avatar';
import type { SelectMenuCreateAction } from 'src/components/SelectMenu/select-menu.types';
import { useConversationParticipantOptions } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { validateEmail } from '..';

const PARTICIPANT_SEARCH_DEBOUNCE_MS = 500;

interface ParticipantSearchItem extends ModalListItem {
  handler: () => void;
}

export const UserSearchModal: React.FC = () => {
  const { isOpened, modalProps } = useModalState(Modals.UserSearchModal);
  const { closeModal } = useModalMethods(Modals.UserSearchModal);

  const { partnerId, onSelect } = modalProps!;

  const [searchValue, setSearchValue] = useState('');

  const { getConversationParticipantOptions, conversationParticipantOptions, loading } =
    useConversationParticipantOptions();

  useEffect(() => {
    getConversationParticipantOptions(
      {
        partnerId,
        searchText: searchValue,
      },
      true,
    );
  }, [partnerId, searchValue, getConversationParticipantOptions]);

  const options = useMemo<ParticipantSearchItem[]>(
    () =>
      conversationParticipantOptions
        .filter(
          (opt) => opt.email && opt.participantEntity !== ConversationParticipantEntity.Rsm,
        )
        .map((participant) => {
          const externalPartnerId =
            participant.entityPartnerId !== partnerId
              ? participant.entityPartnerId
              : undefined;
          const title = isNullish(externalPartnerId)
            ? participant.label
            : `Workspace: ${externalPartnerId} ${participant.label}`;
          return {
            id: participant.id,
            title,
            description: participant.email,
            group: participant.group,
            avatar:
              participant.participantEntity === ConversationParticipantEntity.PlatformUser ? (
                <PlatformUserAvatar
                  partnerId={participant.entityPartnerId}
                  avatarFileKey={participant.avatarFileKey}
                  name={title}
                />
              ) : (
                <ColoredAvatar name={title} />
              ),
            handler: () => onSelect(participant),
          };
        }),
    [conversationParticipantOptions, onSelect, partnerId],
  );

  const addEmail = useMemo<SelectMenuCreateAction | undefined>(
    () =>
      options.length === 0 && searchValue && validateEmail(searchValue)
        ? {
            label: `Add ${searchValue}`,
            onSelect: () => {
              closeModal();
              onSelect(searchValue);
            },
          }
        : undefined,
    [closeModal, onSelect, options.length, searchValue],
  );

  const onItemSelect = useCallback(
    (item: ParticipantSearchItem) => {
      closeModal();
      item.handler();
    },
    [closeModal],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Add people"
      onClose={closeModal}
      data-testid="user-search-modal"
    >
      <ModalList
        items={options}
        onSelect={onItemSelect}
        loading={loading}
        shouldFilter={false}
        onSearchChange={setSearchValue}
        searchDebounce={PARTICIPANT_SEARCH_DEBOUNCE_MS}
        placeholder="Search people or type an email…"
        createAction={addEmail}
        testId="user-search-modal"
      />
    </ModalContainer>
  );
};
